import type { TicketChatMessage } from "../Stores/Ticket";

// Pure author predicates. Deliberately free of store imports so they stay unit
// testable — the stores pull in the whole conversation component tree (and with
// it tiptap), which jest can't transform. The display name, which does need the
// config/session stores, lives in MessageAuthorName.ts.

// Row types that read as a spoken message and therefore carry an author
// header. Cards with their own chrome — the customer's submitted form
// (FEEDBACK_ITEM), video-call and other ACTION cards, bot input replies —
// already name their author inside themselves, so a header above would only
// repeat it. Mirrors `quotableMessageTypes`, but for a different reason: this
// is about who is speaking, not about what can be quoted.
const authoredMessageTypes = [
  "TEXT",
  "SHARED_COMMENT",
  "USER_TEXT",
  "EMAIL_MESSAGE",
  "BOT",
  "BOT_REPLY",
];

// A reply the server sent as the AI rather than as a teammate: a `TEXT` row
// flagged `bot` with no user (Kai's own account writing through the Gleap MCP,
// #148507). Customer rows can carry `bot` too, so the type matters.
export const isBotTeamReply = (message?: TicketChatMessage | null): boolean =>
  !!message && message.type === "TEXT" && message.bot === true && !message.user;

export const isBotAuthored = (message?: TicketChatMessage | null): boolean =>
  !!message &&
  (message.type === "BOT" ||
    !!message.data?.answerBot ||
    isBotTeamReply(message));

// Mirrors `flowConfig.ticketHeaderMode` (Stores/Config.ts). Only "ai-only"
// matters here: the conversation header then shows the AI even after a
// hand-off, and the message rows have to agree with it.
export type TicketHeaderMode = "ai-team" | "ai-only" | "ai-assignee";

// Whether a message should be PRESENTED as the AI agent — its own bot rows,
// plus, under the "ai-only" conversation header, every teammate reply. The
// header already fronts the whole conversation as the AI in that mode
// (WidgetHeader getOperatorInfo); a human name + Gravatar above the reply
// bubbles would contradict it.
export const presentsAsBot = (
  message?: TicketChatMessage | null,
  headerMode: TicketHeaderMode = "ai-team",
): boolean =>
  isBotAuthored(message) ||
  (headerMode === "ai-only" && !!message?.user?.firstName);

// Whether a message has an author worth naming above it. Guards the header
// against rows that have no real author: without it the name resolution falls
// through to the session name, which would label the customer's own form
// submission as if a teammate had written it.
export const hasNamedAuthor = (message?: TicketChatMessage | null): boolean => {
  if (!message || !authoredMessageTypes.includes(message.type ?? "")) {
    return false;
  }

  return isBotAuthored(message) || !!message.user?.firstName;
};

// Stable identity for the author of a message. Two consecutive messages that
// share a key belong to the same visual group; a change of key ends the group
// and opens the next one with a fresh avatar + name header, so a handoff
// between two agents never reads as one continuous message (#142998).
//
// Bot-ness is checked first on purpose: agent replies are written as `BOT`
// comments with no populated `user` (Server `createBotCommentAndNotify`), and
// every bot collapses to one key because `data.agentName` never reaches the
// widget — the server only sets it on `SYSTEM_MESSAGE` rows, which the shared
// message endpoint filters out.
export const getMessageAuthorKey = (
  message?: TicketChatMessage | null,
  headerMode: TicketHeaderMode = "ai-team",
): string => {
  if (!message) {
    return "";
  }

  // Under "ai-only" every teammate reply is shown as the AI, so consecutive
  // replies from different people (or from a person and the bot) form one
  // group — a second header would just repeat the same bot identity.
  if (presentsAsBot(message, headerMode)) {
    return `bot:${message.data?.agentName ?? ""}`;
  }

  if (message.user) {
    // The two shared-message endpoints disagree on the author's id field: the
    // list serializes with `toObject()` and ships only `_id`, the single-message
    // fetch uses `toJSON()` and adds the `id` virtual. Accept either, and fall
    // back to the display name so two teammates never collapse into one group
    // just because the id was missing from the payload.
    const userId = message.user.id ?? (message.user as { _id?: string })._id;
    if (userId) {
      return `user:${userId}`;
    }

    const name =
      `${message.user.firstName ?? ""} ${message.user.lastName ?? ""}`.trim();
    if (name) {
      return `user:${name}`;
    }
  }

  if (message.session?.gleapId) {
    return `session:${message.session.gleapId}`;
  }

  return `type:${message.type ?? ""}`;
};
